/**
 * Shared helpers for table-row image preview and saved-image UUID resolution.
 */
import type { TableRow } from '@shared/components/table/table.model';

export const DEFAULT_TABLE_IMAGE_UUID_FIELDS = ['imageUuid', 'evaluationUuid'] as const;

export function resolveTableRowPreviewUrl(row: TableRow): string | undefined {
  for (const candidate of collectRowCandidates(row)) {
    const url = pickPreviewFromCandidate(candidate);
    if (url) return url;
  }
  return undefined;
}

export function resolveTableRowImageUuid(
  row: TableRow,
  uuidFields: readonly string[] = DEFAULT_TABLE_IMAGE_UUID_FIELDS,
): string | undefined {
  for (const candidate of collectRowCandidates(row)) {
    for (const field of uuidFields) {
      const value = candidate[field];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }
  return undefined;
}

/** Sync saved image metadata from API into a stored row without overwriting a pending local capture. */
export function mergeStoredRowImageFields(
  row: TableRow,
  options: {
    hasImage?: boolean;
    imageUuid?: string | null;
    evaluationUuid?: string | null;
  },
): TableRow {
  const merged = { ...row };
  if (!options.hasImage) {
    return merged;
  }

  merged['hasImage'] = true;
  if (options.imageUuid) {
    merged['imageUuid'] = options.imageUuid;
  }
  if (options.evaluationUuid) {
    merged['evaluationUuid'] = options.evaluationUuid;
  }
  if (!merged['imageFile']) {
    merged['imageFileName'] = '';
    merged['imageDataUrl'] = null;
    merged['imageFile'] = null;
  }
  return merged;
}

function collectRowCandidates(row: TableRow): TableRow[] {
  const candidates: TableRow[] = [row];
  if (row['isChildRow'] && row['_parentRow']) {
    candidates.push(row['_parentRow'] as TableRow);
  }
  if (row['_rowData']) {
    candidates.push(row['_rowData'] as TableRow);
  }
  return candidates;
}

function pickPreviewFromCandidate(candidate: TableRow): string | undefined {
  for (const key of ['imageDataUrl', 'imageUrl', 'previewUrl', 'preview'] as const) {
    const value = candidate[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  const rowData = candidate['_rowData'] as TableRow | undefined;
  if (rowData) {
    for (const key of ['imageUrl', 'imagePath'] as const) {
      const value = rowData[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }

  return undefined;
}
